import { createFileRoute } from "@tanstack/react-router";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import { getProjectBySlug, type ProjectData } from "@/data/projectsData";
import { buildHead, SITE_URL, ORG_ID } from "@/lib/seo-head";

function resolveProject(slug: string | undefined) {
  if (!slug) return null;
  const project = getProjectBySlug(slug);
  return project ? { project } : null;
}

export const Route = createFileRoute("/projects/$slug")({
  head: ({ loaderData }) => {
    const data = loaderData as unknown as { project: ProjectData } | null;
    if (!data) {
      return buildHead({
        title: "Project Not Found | Abacus Design Solutions",
        description:
          "The project case study you are looking for does not exist or has been moved.",
        path: "/projects",
        noIndex: true,
      });
    }
    const p = data.project;
    const title = p.seoTitle || `${p.title} | Abacus Design Solutions Projects`;
    const description = p.seoDescription || p.description;
    return buildHead({
      title,
      description,
      path: `/projects/${p.slug}`,
      ogImage: p.image,
      schemaJson: {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        "@id": `${SITE_URL}/projects/${p.slug}#project`,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${SITE_URL}/projects/${p.slug}`,
        },
        name: p.title,
        description: p.description,
        url: `${SITE_URL}/projects/${p.slug}`,
        image: [p.image, ...(p.additionalImages || [])],
        creator: { "@id": ORG_ID },
        provider: { "@id": ORG_ID },
        ...(p.client ? { about: { "@type": "Brand", name: p.client } } : {}),
        ...(p.location
          ? { spatialCoverage: { "@type": "Place", name: p.location } }
          : {}),
        ...(p.year ? { dateCreated: p.year } : {}),
      },
    });
  },
  loader: ({ params }) => resolveProject(params.slug),
  component: ProjectDetailPage,
});
